/** biome-ignore-all lint/suspicious/noExplicitAny: We need to allow any */

import React, {
  type Ref,
  useRef,
  useEffect,
  type RefCallback,
  cloneElement,
  type ReactElement,
  type HTMLAttributes,
  type MutableRefObject,
  type FunctionComponent,
} from "react";

type FocusEvents = "focusin" | "focusout";
type MouseEvents = "click" | "mousedown" | "mouseup";
type TouchEvents = "touchstart" | "touchend";
type Events = FocusEvent | MouseEvent | TouchEvent;

interface Props extends HTMLAttributes<HTMLElement> {
  onClickAway: (event: Events) => void;
  focusEvent?: FocusEvents;
  mouseEvent?: MouseEvents;
  touchEvent?: TouchEvents;
  children: ReactElement<any>;
}

const eventTypeMapping = {
  click: "onClick",
  focusin: "onFocus",
  focusout: "onBlur",
  mousedown: "onMouseDown",
  mouseup: "onMouseUp",
  touchstart: "onTouchStart",
  touchend: "onTouchEnd",
};

const reactMajorVersion = parseInt(React.version.split(".")[0], 10);

const mergeRefs = <T,>(refs: Array<Ref<T> | undefined | null>): RefCallback<T> => {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as MutableRefObject<T | null>).current = value;
      }
    });
  };
};

const ClickAwayListener: FunctionComponent<Props> = ({
  children,
  onClickAway,
  focusEvent = "focusin",
  mouseEvent = "click",
  touchEvent = "touchend",
  ...rest
}) => {
  const node = useRef<HTMLElement | null>(null);
  const bubbledEventTarget = useRef<EventTarget | null>(null);
  const mountedRef = useRef(false);

  /**
   * Prevents the bubbled event from getting triggered immediately
   * https://github.com/facebook/react/issues/20074
   */
  useEffect(() => {
    setTimeout(() => {
      mountedRef.current = true;
    }, 0);

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleBubbledEvents =
    (type: string) =>
    (event: Events): void => {
      bubbledEventTarget.current = event.target;
      const handler = children?.props[type];

      if (handler) {
        handler(event);
      }
    };

  let childRef: React.Ref<any> | null = null;

  // For React 19+, we get the ref via props.ref
  if (reactMajorVersion >= 19) {
    childRef = children.props?.ref || null;
  } else if ("ref" in children) {
    childRef = (children as any).ref;
  }

  // Create a combined ref handler
  const combinedRef = mergeRefs([node, childRef]);

  useEffect(() => {
    const nodeDocument = node.current?.ownerDocument ?? document;

    const handleEvents = (event: Events): void => {
      if (!mountedRef.current) return;

      const target = event.target as Node;

      // Check if click is inside the main node
      if (node.current?.contains(target)) {
        return;
      }

      // Check if click is inside a portal that belongs to this component
      // Portals can mark themselves with data-click-away-inside attribute
      if (target instanceof Element) {
        const portalParent = target.closest("[data-click-away-inside]");
        if (portalParent) {
          return;
        }
      }

      if (bubbledEventTarget.current === target || !nodeDocument.contains(target)) {
        return;
      }

      onClickAway(event);
    };

    // Use capture phase to ensure we detect clicks even when stopPropagation is called
    nodeDocument.addEventListener(mouseEvent, handleEvents, { capture: true });
    nodeDocument.addEventListener(touchEvent, handleEvents, { capture: true });
    nodeDocument.addEventListener(focusEvent, handleEvents, { capture: true });

    return () => {
      nodeDocument.removeEventListener(mouseEvent, handleEvents, { capture: true });
      nodeDocument.removeEventListener(touchEvent, handleEvents, { capture: true });
      nodeDocument.removeEventListener(focusEvent, handleEvents, { capture: true });
    };
  }, [focusEvent, mouseEvent, onClickAway, touchEvent]);

  const mappedMouseEvent = eventTypeMapping[mouseEvent];
  const mappedTouchEvent = eventTypeMapping[touchEvent];
  const mappedFocusEvent = eventTypeMapping[focusEvent];

  return React.Children.only(
    cloneElement(children as ReactElement<any>, {
      ref: combinedRef,
      [mappedFocusEvent]: handleBubbledEvents(mappedFocusEvent),
      [mappedMouseEvent]: handleBubbledEvents(mappedMouseEvent),
      [mappedTouchEvent]: handleBubbledEvents(mappedTouchEvent),
      ...rest,
    }),
  );
};

ClickAwayListener.displayName = "ClickAwayListener";

export default ClickAwayListener;
